import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { TenantHostService } from '../../services/tenant-host.service';
import { PortalContentService } from '../../services/portal-content.service';
import { LanguageService } from '../../services/language.service';
import { SahredModule } from '../../common';
import { PortalItemDto } from '../../models/portal';

interface SocialLink { key: string; icon: string; url: string; }

/**
 * Public portal shell shown on the apex host (no tenant subdomain).
 *
 * Provides the top navigation between the two public experiences — "Become a
 * Tenant" (onboarding) and "Find a Teacher" (search) — via a <router-outlet>,
 * plus a "Sign in" affordance that asks an existing customer for their academy
 * address and forwards them to that tenant's subdomain login.
 */
@Component({
  selector: 'app-public-shell',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SahredModule],
  templateUrl: './public-shell.component.html',
  styleUrls: ['./public-shell.component.scss'],
})
export class PublicShellComponent implements OnInit {
  /** Suffix shown next to the academy-address input, e.g. ".menassaty.com". */
  readonly domainSuffix = '.' + (environment.baseDomain || 'localhost');

  showSignIn = false;
  academy = '';
  signInError = '';

  /** Set when redirected here because a requested academy subdomain doesn't exist. */
  notFoundAcademy: string | null = null;

  readonly year = new Date().getFullYear();

  // CMS-managed header/footer
  headerLinks: PortalItemDto[] = [];
  footerLinks: PortalItemDto[] = [];
  private contentMap: Record<string, { en?: string | null; ar?: string | null }> = {};

  constructor(
    private host: TenantHostService,
    private route: ActivatedRoute,
    private cms: PortalContentService,
    private language: LanguageService,
    private translate: TranslateService,
  ) {
    this.notFoundAcademy = this.route.snapshot.queryParamMap.get('academy');
  }

  ngOnInit(): void {
    this.cms.getPublic().subscribe({
      next: (res) => {
        if (!res.isSuccess || !res.value) return;
        this.contentMap = {};
        (res.value.content ?? []).forEach((c) => (this.contentMap[c.key] = { en: c.valueEn, ar: c.valueAr }));
        this.headerLinks = (res.value.headerLinks ?? []).filter((l) => l.linkUrl && this.pick(l.titleEn, l.titleAr));
        this.footerLinks = (res.value.footerLinks ?? []).filter((l) => l.linkUrl && this.pick(l.titleEn, l.titleAr));
      },
      error: () => {},
    });
  }

  // ── localization + content helpers ──────────────────────────────────────────
  // Read through the service (not localStorage) so CMS copy re-picks the moment
  // the language toggle fires, without a reload.
  private get lang(): string { return this.language.current; }
  private pick(en?: string | null, ar?: string | null): string {
    return (this.lang === 'ar' ? ar || en : en || ar) ?? '';
  }
  /** Localized CMS content by key, with a fallback. */
  c(key: string, fallback = ''): string {
    const v = this.contentMap[key];
    const picked = v ? this.pick(v.en, v.ar) : '';
    return picked || fallback;
  }
  /** Label for a CMS link item in the active language. */
  linkLabel(l: PortalItemDto): string { return this.pick(l.titleEn, l.titleAr); }

  /** True external links (http/https) open in a new tab; internal ones route. */
  isExternal(url?: string | null): boolean { return !!url && /^https?:\/\//i.test(url); }

  /** Configured social links (only those with a URL set). */
  get socials(): SocialLink[] {
    const defs: Omit<SocialLink, 'url'>[] = [
      { key: 'footer.facebook', icon: 'bi-facebook' },
      { key: 'footer.twitter', icon: 'bi-twitter-x' },
      { key: 'footer.linkedin', icon: 'bi-linkedin' },
      { key: 'footer.instagram', icon: 'bi-instagram' },
    ];
    return defs
      .map((d) => ({ ...d, url: this.c(d.key) }))
      .filter((s) => !!s.url);
  }

  get footerEmail(): string { return this.c('footer.email'); }
  get footerPhone(): string { return this.c('footer.phone'); }
  get footerAbout(): string { return this.c('footer.about'); }
  get footerCopyright(): string {
    return this.c('footer.copyright') || `© ${this.year} ${this.translate.instant('portal.Copyright')}`;
  }

  toggleSignIn(): void {
    this.showSignIn = !this.showSignIn;
    this.signInError = '';
  }

  goToTenant(): void {
    const sub = this.academy.trim().toLowerCase();
    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(sub)) {
      this.signInError = this.translate.instant('portal.InvalidAcademy');
      return;
    }
    // Hard navigation so the new origin loads its own tenant context.
    window.location.href = this.host.buildTenantLoginUrl(sub);
  }
}
